import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/auth';

const Invitations = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selected, setSelected] = useState({});
  const [toast, setToast] = useState(null);
  const [filterRole, setFilterRole] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => { fetchPending(); }, []);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const showToast = (message, type = 'success') => setToast({ message, type });

  const fetchPending = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.get('/admin/invitations/pending', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(Array.isArray(res.data) ? res.data : []);
      const sel = {};
      (res.data || []).forEach((u) => { sel[u.id] = false; });
      setSelected(sel);
    } catch (err) {
      console.error(err);
      showToast('Erreur de chargement', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => setSelected((p) => ({ ...p, [id]: !p[id] }));

  const toggleAll = () => {
    const filtered = filteredUsers;
    const allSelected = filtered.every((u) => selected[u.id]);
    const next = { ...selected };
    filtered.forEach((u) => { next[u.id] = !allSelected; });
    setSelected(next);
  };

  const handleSend = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) {
      showToast('Sélectionnez au moins 1 utilisateur', 'error');
      return;
    }
    if (!window.confirm(`Envoyer ${ids.length} invitation(s) ?`)) return;

    try {
      setSending(true);
      const token = localStorage.getItem('token');
      const res = await api.post('/admin/invitations/send', {
        user_ids: ids.map(Number),
      }, { headers: { Authorization: `Bearer ${token}` } });
      showToast(`✅ ${res.data.message}`, 'success');
      fetchPending();
    } catch (err) {
      showToast('❌ ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setSending(false);
    }
  };

  const handleSendAll = async () => {
    if (users.length === 0) { showToast('Aucun utilisateur', 'error'); return; }
    if (!window.confirm(`Envoyer ${users.length} invitation(s) à TOUS ?`)) return;

    try {
      setSending(true);
      const token = localStorage.getItem('token');
      const res = await api.post('/admin/invitations/send', {
        user_ids: users.map((u) => u.id),
      }, { headers: { Authorization: `Bearer ${token}` } });
      showToast(`✅ ${res.data.message}`, 'success');
      fetchPending();
    } catch (err) {
      showToast('❌ ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setSending(false);
    }
  };

  const filteredUsers = users.filter((u) => {
    if (filterRole !== 'all' && u.role !== filterRole) return false;
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      if (!u.name.toLowerCase().includes(t) && !u.email.toLowerCase().includes(t)) return false;
    }
    return true;
  });

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const studentsCount = users.filter((u) => u.role === 'student').length;
  const parentsCount = users.filter((u) => u.role === 'parent').length;
  const teachersCount = users.filter((u) => u.role === 'teacher' || u.role === 'principal_teacher').length;

  return (
    <div className="p-6 lg:p-8 bg-white min-h-screen">
      {toast && (
        <div className={`fixed top-4 right-4 z-[100] ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'} text-white px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 min-w-[280px]`}>
          <span className="text-xl">{toast.type === 'success' ? '✅' : '❌'}</span>
          <span className="flex-1 font-medium text-sm">{toast.message}</span>
          <button onClick={() => setToast(null)} className="text-white text-2xl font-bold leading-none">×</button>
        </div>
      )}

      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#0A0A0A]">📧 Invitations</h1>
          <p className="text-gray-500 mt-1">Envoyez les identifiants par email aux élèves, parents et professeurs</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => navigate('/admin/students')} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium">← Retour</button>
          <button onClick={handleSendAll} disabled={sending || users.length === 0} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50">
            {sending ? '⏳...' : `📤 Envoyer à tous (${users.length})`}
          </button>
          <button onClick={handleSend} disabled={sending || selectedCount === 0} className="px-4 py-2 bg-[#F39C12] text-white rounded-lg hover:bg-[#E67E22] font-bold disabled:opacity-50">
            {sending ? '⏳...' : `📧 Envoyer (${selectedCount})`}
          </button>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <p className="text-xs text-gray-500 mb-1">👥 À inviter</p>
          <p className="text-2xl font-bold text-[#0A0A0A]">{users.length}</p>
        </div>
        <div className="bg-white rounded-2xl border border-blue-100 p-5">
          <p className="text-xs text-gray-500 mb-1">🎓 Élèves</p>
          <p className="text-2xl font-bold text-blue-600">{studentsCount}</p>
        </div>
        <div className="bg-white rounded-2xl border border-orange-100 p-5">
          <p className="text-xs text-gray-500 mb-1">👨‍👩‍👧 Parents</p>
          <p className="text-2xl font-bold text-orange-600">{parentsCount}</p>
        </div>
        <div className="bg-white rounded-2xl border border-purple-100 p-5">
          <p className="text-xs text-gray-500 mb-1">👨‍🏫 Professeurs</p>
          <p className="text-2xl font-bold text-purple-600">{teachersCount}</p>
        </div>
        <div className="bg-white rounded-2xl border border-green-100 p-5">
          <p className="text-xs text-gray-500 mb-1">📤 Sélectionnés</p>
          <p className="text-2xl font-bold text-green-600">{selectedCount}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input type="text" placeholder="🔍 Rechercher..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="px-4 py-2 border rounded-lg focus:outline-none focus:border-[#F39C12]" />
          <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} className="px-4 py-2 border rounded-lg">
            <option value="all">Tous les rôles</option>
            <option value="student">🎓 Élèves</option>
            <option value="parent">👨‍👩‍👧 Parents</option>
            <option value="teacher">👨‍🏫 Professeurs</option>
            <option value="principal_teacher">👑 Profs principaux</option>
          </select>
          <div className="text-sm text-gray-500 flex items-center px-2">{filteredUsers.length} utilisateur(s)</div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-gray-500">Chargement...</div>
      ) : users.length === 0 ? (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-12 text-center">
          <p className="text-5xl mb-3">🎉</p>
          <p className="text-lg font-bold text-green-700 mb-2">Tous les utilisateurs sont activés !</p>
          <p className="text-sm text-gray-600">Aucune invitation en attente.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-3">
                    <input type="checkbox" checked={filteredUsers.length > 0 && filteredUsers.every((u) => selected[u.id])} onChange={toggleAll} className="w-4 h-4" />
                  </th>
                  <th className="text-left py-3 px-3 text-xs font-semibold text-gray-600">Nom</th>
                  <th className="text-left py-3 px-3 text-xs font-semibold text-gray-600">Email</th>
                  <th className="text-left py-3 px-3 text-xs font-semibold text-gray-600">Rôle</th>
                  <th className="text-left py-3 px-3 text-xs font-semibold text-gray-600">Statut</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.id} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-3">
                      <input type="checkbox" checked={!!selected[u.id]} onChange={() => toggleSelect(u.id)} className="w-4 h-4" />
                    </td>
                    <td className="py-3 px-3 text-sm font-medium">{u.name}</td>
                    <td className="py-3 px-3 text-xs font-mono">{u.email}</td>
                    <td className="py-3 px-3">
                      {u.role === 'student' ? (
                        <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">🎓 Élève</span>
                      ) : u.role === 'parent' ? (
                        <span className="px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-700">👨‍👩‍👧 Parent</span>
                      ) : u.role === 'teacher' ? (
                        <span className="px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-700">👨‍🏫 Prof</span>
                      ) : u.role === 'principal_teacher' ? (
                        <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-700">👑 Prof. Principal</span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">{u.role}</span>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      {u.invitation_sent_at ? (
                        <span className="text-xs text-gray-600">📤 Envoyé le {new Date(u.invitation_sent_at).toLocaleDateString('fr-FR')}</span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-700">⏳ En attente</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invitations;